import { useCallback, useEffect, useMemo, useState } from 'react';
import UniversalPlaceholder from '../../Layout/UniversalPlaceholder';
import { useNavigate } from 'react-router-dom';
import {
  withLoading,
  useTranslator,
  CookbookFilterMeal,
  CookbookFilterTag,
  CookbookMealListEdge,
  CookbookPreparationTime,
} from '@front-activerse/common-components';
import { Container, Row, Col, Button, Spinner } from 'reactstrap';
import { Layout } from '../../Layout';
import TitleBar from '../../Layout/TitleBar';
import FilterOptionGroup, { FilterOption, FilterOptionId } from './FilterOptionGroup';

/**
 * Labels of the preparation-time options. NONE is deliberately missing: meals that need no
 * preparation are offered under the lowest option instead of getting one of their own.
 */
const PREPARATION_TIME_NAMES: Record<
  Exclude<CookbookPreparationTime, CookbookPreparationTime.NONE>,
  string
> = {
  [CookbookPreparationTime.UP_TO_5_MINUTES]: '< 5 min',
  [CookbookPreparationTime.UP_TO_15_MINUTES]: '< 15 min',
  [CookbookPreparationTime.UP_TO_30_MINUTES]: '< 30 min',
  [CookbookPreparationTime.UP_TO_45_MINUTES]: '< 45 min',
  [CookbookPreparationTime.UP_TO_AN_HOUR]: '< 60 min',
  [CookbookPreparationTime.OVER_AN_HOUR]: '> 60 min',
};

/** How far a meal's kcal may sit from the picked value and still match — same as the server. */
const KCAL_TOLERANCE = 50;

const FILTERED_MEALS_PATH = '/meals/cookbook/filtered-meals';

const FILTER_GROUPS = [
  'mealTypeTags',
  'dietTypeTags',
  'customTags',
  'kcal',
  'preparationTime',
] as const;

type FilterGroup = (typeof FILTER_GROUPS)[number];

type ActiveFilterIds = Record<FilterGroup, FilterOptionId[]>;

type MatchedOptionIds = (meal: CookbookFilterMeal, group: FilterGroup) => FilterOptionId[];

const EMPTY_ACTIVE_FILTER_IDS: ActiveFilterIds = {
  mealTypeTags: [],
  dietTypeTags: [],
  customTags: [],
  kcal: [],
  preparationTime: [],
};

/**
 * Builds a function returning the ids of the options a meal matches within each group. Tag groups
 * yield the meal's own tag ids; kcal yields every range the meal lands within KCAL_TOLERANCE of, so
 * a single meal can match more than one; preparation time yields the meal's bucket, if it has one.
 * Typing the extractors as a full Record means adding a group to FILTER_GROUPS without teaching it
 * how to match is a compile error.
 */
const createMatchedOptionIds = (calorieRanges: number[]): MatchedOptionIds => {
  const extractors: Record<FilterGroup, (meal: CookbookFilterMeal) => FilterOptionId[]> = {
    mealTypeTags: (meal) => meal.mealTypeTags,
    dietTypeTags: (meal) => meal.dietTypeTags,
    customTags: (meal) => meal.customTags,
    kcal: (meal) => calorieRanges.filter((range) => Math.abs(meal.kcal - range) <= KCAL_TOLERANCE),
    preparationTime: (meal) => {
      if (!meal.preparationTime) {
        return [];
      }

      return [
        meal.preparationTime === CookbookPreparationTime.NONE
          ? CookbookPreparationTime.UP_TO_5_MINUTES
          : meal.preparationTime,
      ];
    },
  };

  return (meal, group) => extractors[group](meal);
};

/** OR within a group: a group with nothing selected adds no constraint. */
const matchesFilterGroup = (
  matchedOptionIds: MatchedOptionIds,
  meal: CookbookFilterMeal,
  group: FilterGroup,
  activeInGroup: FilterOptionId[]
) =>
  activeInGroup.length === 0 ||
  matchedOptionIds(meal, group).some((optionId) => activeInGroup.includes(optionId));

/**
 * Option ids still worth showing in `group` — collected from meals that satisfy every OTHER group.
 * The group's own selection is ignored on purpose: options inside a group are OR-ed, so picking
 * another one only widens the result and must never disable its siblings.
 */
const selectableOptionIdsInGroup = (
  matchedOptionIds: MatchedOptionIds,
  meals: CookbookFilterMeal[],
  group: FilterGroup,
  activeFilterIds: ActiveFilterIds
) =>
  new Set(
    meals
      .filter((meal) =>
        FILTER_GROUPS.every(
          (otherGroup) =>
            otherGroup === group ||
            matchesFilterGroup(matchedOptionIds, meal, otherGroup, activeFilterIds[otherGroup])
        )
      )
      .flatMap((meal) => matchedOptionIds(meal, group))
  );

type CookbookFilterContainerProps = {
  calorieRanges: number[];
  mealTypeTags: CookbookFilterTag[];
  dietTypeTags: CookbookFilterTag[];
  customTags: CookbookFilterTag[];
  filterMeals: CookbookFilterMeal[];
  /** Full meal list, loaded in the background; undefined until it arrives. */
  meals?: CookbookMealListEdge[];
};

const CookbookFilterContainer = ({
  calorieRanges,
  mealTypeTags,
  dietTypeTags,
  customTags,
  filterMeals,
  meals,
}: CookbookFilterContainerProps) => {
  const { t } = useTranslator();
  const navigate = useNavigate();

  const matchedOptionIds = useMemo(() => createMatchedOptionIds(calorieRanges), [calorieRanges]);

  const kcalOptions = useMemo<FilterOption[]>(
    () => calorieRanges.map((range) => ({ id: range, name: `${range} kcal` })),
    [calorieRanges]
  );

  const preparationTimeOptions = useMemo<FilterOption[]>(
    () => Object.entries(PREPARATION_TIME_NAMES).map(([id, name]) => ({ id, name })),
    []
  );

  const [activeFilterIds, setActiveFilterIds] = useState<ActiveFilterIds>(EMPTY_ACTIVE_FILTER_IDS);
  const [isWaitingForMeals, setIsWaitingForMeals] = useState(false);

  const handleOptionClick = (group: FilterGroup, optionId: FilterOptionId) => {
    setActiveFilterIds((prevActiveFilterIds) => {
      const activeInGroup = prevActiveFilterIds[group];

      return {
        ...prevActiveFilterIds,
        [group]: activeInGroup.includes(optionId)
          ? activeInGroup.filter((id) => id !== optionId)
          : [...activeInGroup, optionId],
      };
    });
  };

  // AND between groups (every), OR within a group (matchesFilterGroup).
  const filteredMealIds = useMemo(
    () =>
      filterMeals
        .filter((meal) =>
          FILTER_GROUPS.every((group) =>
            matchesFilterGroup(matchedOptionIds, meal, group, activeFilterIds[group])
          )
        )
        .map((meal) => meal.id),
    [filterMeals, matchedOptionIds, activeFilterIds]
  );

  // An option is disabled when no meal matches it once the other groups' filters are applied.
  const disabledFilterIds = useMemo<ActiveFilterIds>(() => {
    const disabledIdsIn = (group: FilterGroup, options: FilterOption[]) => {
      const selectableIds = selectableOptionIdsInGroup(
        matchedOptionIds,
        filterMeals,
        group,
        activeFilterIds
      );

      return options.filter((option) => !selectableIds.has(option.id)).map((option) => option.id);
    };

    return {
      mealTypeTags: disabledIdsIn('mealTypeTags', mealTypeTags),
      dietTypeTags: disabledIdsIn('dietTypeTags', dietTypeTags),
      customTags: disabledIdsIn('customTags', customTags),
      kcal: disabledIdsIn('kcal', kcalOptions),
      preparationTime: disabledIdsIn('preparationTime', preparationTimeOptions),
    };
  }, [
    matchedOptionIds,
    filterMeals,
    activeFilterIds,
    mealTypeTags,
    dietTypeTags,
    customTags,
    kcalOptions,
    preparationTimeOptions,
  ]);

  const showResults = useCallback(
    (allMeals: CookbookMealListEdge[]) => {
      const filteredMealIdSet = new Set(filteredMealIds);

      navigate(FILTERED_MEALS_PATH, {
        state: { meals: allMeals.filter((meal) => filteredMealIdSet.has(meal.node.id)) },
      });
    },
    [filteredMealIds, navigate]
  );

  const handleShowResultsClick = () => {
    if (!meals) {
      setIsWaitingForMeals(true);
      return;
    }

    showResults(meals);
  };

  // The user asked for results before the full meal list arrived — show them as soon as it does.
  // SlideRoutes keeps this page mounted after navigating away, so the waiting state must be reset
  // here, otherwise going back would show a spinner stuck on the button.
  useEffect(() => {
    if (isWaitingForMeals && meals) {
      setIsWaitingForMeals(false);
      showResults(meals);
    }
  }, [isWaitingForMeals, meals, showResults]);

  const showResultsLabel =
    filteredMealIds.length > 0
      ? t('cookbook.filters.buttonShowResult', { count: filteredMealIds.length })
      : t('cookbook.filters.noResults.button');

  return (
    <Layout>
      <TitleBar level={1}>
        {t('cookbook.filters.navbarTitle')}
        <div className="title-bar-icons">
          <Button
            color="link"
            className="p-0"
            onClick={() => setActiveFilterIds(EMPTY_ACTIVE_FILTER_IDS)}
          >
            {t('cookbook.filters.clearButton.navbar')}
          </Button>
        </div>
      </TitleBar>
      <div className="page-template-cookbook">
        <Container className="py-md-4">
          <Row>
            <Col xl={{ size: 10, offset: 1 }}>
              <div className="page-container">
                <div className="cookbook-container">
                  <FilterOptionGroup
                    title={t('cookbook.filters.preparationTime.title')}
                    options={preparationTimeOptions}
                    activeIds={activeFilterIds.preparationTime}
                    disabledIds={disabledFilterIds.preparationTime}
                    columns={3}
                    onToggle={(optionId) => handleOptionClick('preparationTime', optionId)}
                  />

                  <FilterOptionGroup
                    title={t('cookbook.filters.mealType.title')}
                    options={mealTypeTags}
                    activeIds={activeFilterIds.mealTypeTags}
                    disabledIds={disabledFilterIds.mealTypeTags}
                    onToggle={(optionId) => handleOptionClick('mealTypeTags', optionId)}
                  />

                  <FilterOptionGroup
                    title={t('cookbook.filters.dietType.title')}
                    options={dietTypeTags}
                    activeIds={activeFilterIds.dietTypeTags}
                    disabledIds={disabledFilterIds.dietTypeTags}
                    onToggle={(optionId) => handleOptionClick('dietTypeTags', optionId)}
                  />

                  <FilterOptionGroup
                    title={t('cookbook.filters.calories.title')}
                    options={kcalOptions}
                    activeIds={activeFilterIds.kcal}
                    disabledIds={disabledFilterIds.kcal}
                    columns={3}
                    onToggle={(optionId) => handleOptionClick('kcal', optionId)}
                  />

                  <FilterOptionGroup
                    title={t('cookbook.filters.customTag.title')}
                    options={customTags}
                    activeIds={activeFilterIds.customTags}
                    disabledIds={disabledFilterIds.customTags}
                    onToggle={(optionId) => handleOptionClick('customTags', optionId)}
                  />
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
      <div className="sticky-footer">
        <Container className="text-center">
          <Row>
            <Col md={{ size: 4, offset: 4 }}>
              <Button
                color="primary"
                className="w-100"
                disabled={filteredMealIds.length === 0 || isWaitingForMeals}
                onClick={handleShowResultsClick}
              >
                {isWaitingForMeals && (
                  <Spinner size="sm" className="me-2">
                    Loading...
                  </Spinner>
                )}
                {showResultsLabel}
              </Button>
            </Col>
          </Row>
        </Container>
      </div>
    </Layout>
  );
};

const Placeholder = () => <UniversalPlaceholder titleBar="" />;

export default withLoading(CookbookFilterContainer, Placeholder);
